import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Plus, Users, Activity, Coins, AlertTriangle } from "lucide-react";
import { getCreatorDashboard } from "@/api";
import { useApp } from "@/App";
import { useHubSession, errMsg } from "@/hooks/useHubSession";
import { HubAvatar, TierChip } from "@/components/hubs/HubCard";
import { HubClaimPanel } from "@/components/hubs/HubClaimPanel";
import { CopyButton } from "@/components/CopyButton";

const KIND = { milestone: "Milestone", engagement: "Engagement", referral: "Referral" };

function MyHub({ h }) {
  const invite = `${window.location.origin}/hubs/${h.slug}?invite=${h.invite_code}`;
  return (
    <div className="card-tile" data-testid={`creator-hub-${h.slug}`}>
      <div className="flex items-center gap-3">
        <HubAvatar path={h.avatar} name={h.name} size="w-12 h-12" />
        <div className="flex-1 min-w-0"><Link to={`/hubs/${h.slug}`} className="font-display text-lg font-bold hover:text-orange-300 truncate block">{h.name}</Link><TierChip tier={h.tier} multiplier={h.multiplier} tid={`creator-hub-tier-${h.slug}`} /></div>
        {h.open_flags > 0 && <span className="chip border-amber-500/50 text-amber-300" data-testid={`creator-hub-flagged-${h.slug}`}><AlertTriangle size={12} /> Under review</span>}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
        <div><div className="text-xs text-slate-500 flex items-center gap-1"><Users size={12} /> Members</div><div className="font-mono" data-testid={`creator-hub-members-${h.slug}`}>{h.member_count} <span className="text-xs text-slate-500">({h.eligible_members} qualified)</span></div></div>
        <div><div className="text-xs text-slate-500 flex items-center gap-1"><Activity size={12} /> Active today</div><div className="font-mono" data-testid={`creator-hub-dau-${h.slug}`}>{h.daily_active}</div></div>
        <div><div className="text-xs text-slate-500 flex items-center gap-1"><Coins size={12} /> Earned</div><div className="font-mono text-orange-300" data-testid={`creator-hub-earned-${h.slug}`}>{h.total_earned.toLocaleString()}</div></div>
      </div>
      {h.next_milestone && <div className="mt-3 text-xs text-slate-400">Next: {h.next_milestone.bonus.toLocaleString()} FLAME at {h.next_milestone.members} qualified members</div>}
      <div className="mt-3 flex items-center gap-2"><code className="text-[11px] break-all flex-1 text-slate-400">{invite}</code><CopyButton value={invite} tid={`creator-hub-invite-copy-${h.slug}`} label="invite link" /></div>
    </div>
  );
}

export default function CreatorDashboardPage() {
  const { showToast } = useApp();
  const { address, token, withSession, signIn } = useHubSession();
  const [d, setD] = useState(null);
  const load = useCallback(() => { if (token) withSession(getCreatorDashboard).then(setD).catch((e) => showToast(errMsg(e), "error")); }, [token, withSession, showToast]);
  useEffect(() => { load(); }, [load]);

  if (!address) return <div className="pt-32 text-center text-slate-400" data-testid="creator-connect-prompt">Connect your wallet to open your Creator Dashboard.</div>;
  if (!token) return (
    <div className="pt-32 text-center" data-testid="creator-signin-prompt">
      <p className="text-slate-300">Sign a message to prove this wallet is yours. It's free and doesn't send a transaction.</p>
      <button onClick={() => signIn().catch((e) => showToast(errMsg(e), "error"))} className="btn-tactile btn-flame mt-4" data-testid="creator-signin-button">Sign in with wallet</button>
    </div>
  );
  if (!d) return <div className="pt-32 text-center text-slate-400">Loading…</div>;
  return (
    <div className="pt-24 pb-20" data-testid="creator-dashboard-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1"><div className="chip inline-flex text-orange-300 border-orange-500/40"><LayoutDashboard size={12} /> Creator Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black mt-3">Your hubs</h1></div>
          <Link to="/hubs/new" className="btn-tactile btn-flame flex items-center gap-2" data-testid="creator-new-hub-button"><Plus size={16} /> New hub</Link>
        </div>
        <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          <div className="space-y-4">
            {d.hubs.length === 0 && <div className="card-tile text-slate-400" data-testid="creator-no-hubs">You haven't launched a hub yet. <Link to="/hubs/new" className="text-orange-300 underline">Launch one</Link> to start earning.</div>}
            {d.hubs.map((h) => <MyHub key={h.id} h={h} />)}
            <div className="card-tile" data-testid="creator-ledger">
              <h2 className="font-display text-xl font-bold">Recent earnings</h2>
              {d.ledger.length === 0 && <p className="text-sm text-slate-500 mt-2">Nothing yet.</p>}
              {d.ledger.map((e) => (
                <div key={e.id} className="flex items-center gap-3 py-2 border-b border-white/5 text-sm">
                  <span className="chip text-[10px]">{KIND[e.kind] || e.kind}</span><span className="flex-1 truncate text-slate-400">{e.hub_name}{e.meta?.action ? ` · ${e.meta.action}` : ""}{e.meta?.members ? ` · ${e.meta.members} members` : ""}</span>
                  <span className={`text-xs ${e.status === "credited" ? "text-emerald-300" : "text-amber-300"}`}>{e.status}</span><span className="font-mono w-20 text-right">{e.amount}</span>
                </div>
              ))}
            </div>
          </div>
          <HubClaimPanel d={d} token={token} address={address} showToast={showToast} onDone={load} />
        </div>
      </div>
    </div>
  );
}
